import { bgForLevel, toCss, type BackgroundParams } from '@puzzlemania/background-gen';
import type { GameBackgroundCss } from '@puzzlemania/core';

/**
 * Module 34 (WP-34.3) — level→background service.
 *
 * Turns the build-time background-pool manifests (WP-34.2) plus the assignment
 * engine (WP-34.1 `bgForLevel`) into a single callback the composition root
 * injects into games via `IGameContext.getLevelBackground`. Pure w.r.t. its
 * inputs: the actual `virtual:bg-pool/<game>` manifests are imported by the
 * composition root and passed in here, so this module has no build-time coupling
 * and is unit-testable with fabricated manifests.
 *
 * For a game with a non-empty pool it returns the inline CSS for the assigned
 * background; otherwise `null`, signalling the game to use its built-in default.
 * Layer-1 DOM only — it computes CSS, it never renders.
 */

/** The manifest shape emitted by `virtual:bg-pool/<game>` (WP-34.2). */
export interface BgPoolManifest {
  pool: string[];
  library: Record<string, BackgroundParams>;
}

export type GetLevelBackground = (game: string, level: number) => GameBackgroundCss | null;

export function createLevelBackgroundService(
  manifests: Record<string, BgPoolManifest>,
): GetLevelBackground {
  return (game, level) => {
    const manifest = manifests[game];
    if (!manifest || manifest.pool.length === 0) return null;

    const id = bgForLevel(manifest.pool, level);
    const params = manifest.library[id];
    if (!params) return null; // pool/library out of sync — fall back rather than throw

    const css = toCss(params);
    return {
      backgroundImage: css.backgroundImage,
      backgroundSize: css.backgroundSize,
      backgroundRepeat: css.backgroundRepeat,
      backgroundPosition: css.backgroundPosition,
    };
  };
}
